import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Input, Card, Select, message, Modal } from "antd";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import api from "../interceptors/api";
import StandardButton from "../components/StandardButton";
import ProtectedToolWrapper from "../components/ProtectedToolWrapper";

const { TextArea } = Input;
const { Option } = Select;

export default function UrlScraper() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [url, setUrl] = useState("https://jsonplaceholder.typicode.com");
  const [mode, setMode] = useState("text");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [loginModalVisible, setLoginModalVisible] = useState(false);
  const [pendingShareId, setPendingShareId] = useState(null);

  const checkAuth = () => {
    const userProfileData = JSON.parse(localStorage.getItem('userProfileData') || 'null');
    return userProfileData?.id;
  };

  const loadSharedData = (shareId) => {
    api.get(`/url-scrape/share/${shareId}`)
      .then(res => {
        setUrl(res.data.url || "https://jsonplaceholder.typicode.com");
        setMode(res.data.mode || "text");
        setResult(res.data.result || null);
        toast.success(t("shared_configuration_loaded"));
      })
      .catch(() => toast.error(t("invalid_share_link")));
  };

  useEffect(() => {
    const shareId = searchParams.get("share");
    if (shareId) {
      loadSharedData(shareId);
    }
  }, [searchParams]);

  const handleScrape = async () => {
    if (!url.trim()) {
      toast.error(t("please_enter_url"));
      return;
    }

    const loadingToast = toast.loading(t("scraping_started"));
    setLoading(true);
    setResult(null);
    
    try {
      const userProfileData = JSON.parse(localStorage.getItem('userProfileData') || 'null');

      if (!userProfileData?.id) {
        toast.error(t("login_required"));
        setLoading(false);
        toast.dismiss(loadingToast);
        return;
      }

      const res = await api.post("/url-scrape", {
        url,
        mode
      });
      
      setResult(res.data);
      toast.success(t("scraping_completed"), { id: loadingToast });
    } catch (err) {
      toast.error(err.response?.data?.error || t("api_request_failed"), { id: loadingToast });
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    try {
      const textToCopy = result?.html || result?.text || "";
      await navigator.clipboard.writeText(textToCopy);
      toast.success(t("copy_success"));
    } catch {
      toast.error(t("failed_to_copy"));
    }
  };

  const handleShare = async () => {
    if (!url || !result) {
      toast.error(t("no_content_to_share"));
      return;
    }

    try {
      const res = await api.post("/url-scrape/share", {
        url,
        mode,
        result
      });
      
      if (res.data?.id) {
        const shareUrl = `${window.location.origin}/tools/url-scraper?share=${res.data.id}`;
        await navigator.clipboard.writeText(shareUrl);
        toast.success(t("share_link_copied"));
      }
    } catch (err) {
      toast.error(t("failed_to_share") + ": " + (err.response?.data?.error || err.message));
    }
  };

  return (
    <ProtectedToolWrapper showShareBanner={!!searchParams.get("share")}>
      <main>
        <Card className="max-w-5xl mx-auto my-8 p-6 space-y-6">
          {/* Controls Row */}
          <div className="mb-6">
            <div className="flex flex-wrap gap-4 items-center">
              <Select
                value={mode}
                onChange={setMode}
                style={{ width: 120, height: 40 }}
                className="h-10"
                size="large"
              >
                <Option value="html">{t("HTML")}</Option>
                <Option value="text">{t("Text")}</Option>
              </Select>

              <Input
                placeholder={t("enter_url_https")}
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                size="large"
                className="flex-1 min-w-[200px]"
              />

              <StandardButton
                onClick={handleScrape}
                loading={loading}
                type="secondary"
                className="h-10"
              >
                {t("scrape")}
              </StandardButton>
            </div>
          </div>

          {/* Result */}
          {result && (
            <div className="space-y-4">
              <div>
                <strong>{t("title")}:</strong> {result.title}
              </div>
              <TextArea
                value={result.html || result.text}
                rows={18}
                readOnly
                style={{ fontFamily: "monospace" }}
              />
              <div className="mt-4 flex gap-3">
                <StandardButton
                  onClick={handleCopy}
                  type="secondary"
                  size="small"
                >
                  {t('copy_button')}
                </StandardButton>
                <StandardButton
                  onClick={handleShare}
                  type="secondary"
                  size="small"
                >
                  {t('share')}
                </StandardButton>
              </div>
            </div>
          )}
        </Card>
      </main>
    </ProtectedToolWrapper>
  );
}